import { format } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Button, Icon, Label, Modal } from "semantic-ui-react";

import { Can } from "components";
import { DATE_FORMATS_FNS } from "components/DatePicker";
import { usePermanentLocations, usePermanentRealtimeDealers } from "components/DealersAndLocationsDropdown/hooks";
import "components/NewsModal/NewsModal.scss";
import { useNewsCategoriesQuery } from "modules/NewsDashboard/hooks";
import { PATHS } from "router/paths";

import type { Article } from "models";

type NewsModalProps = {
  article?: Article | null;
  articles?: Article[];
  isOpen: boolean;
  onClose: () => void;
};

export const NewsModal = ({ article, articles, isOpen, onClose }: NewsModalProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { dealers } = usePermanentRealtimeDealers();
  const { locations } = usePermanentLocations();
  const { data: categories } = useNewsCategoriesQuery();
  const [currentIndex, setCurrentIndex] = useState(0);

  const articlesToDisplay = articles || (article ? [article] : []);
  const currentArticle = articlesToDisplay[currentIndex];

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < articlesToDisplay.length - 1;

  const showPagination = articles && articles.length > 1;

  const getDealers = (dealerIds?: number[]) => {
    if (!dealerIds?.length) return null;

    const dealerNames = dealerIds.map(id => dealers.find(d => d.id === id)?.name).filter(Boolean);

    return dealerNames.join(", ");
  };

  const getLocations = (locationIds?: number[]) => {
    if (!locationIds?.length) return null;

    const locationNames = locationIds.map(id => locations.find(location => location.id === id)?.name).filter(Boolean);

    return locationNames.join(", ");
  };

  const getLocationNames = (item: Article) => getLocations(item.location_ids) || "";
  const getDealerNames = (item: Article) => (item.visible_to_all ? t("v8_all_dealers", "All dealers") : getDealers(item.dealer_ids) || "");

  const getCategoryLabel = (item: Article) => {
    if (!item.category_id || !categories) return null;
    const category = categories.find(cat => cat.id === item.category_id);
    return t(`v8_${category?.name}`) || category?.name || "";
  };

  const handleGoToNext = () => hasNext && setCurrentIndex(currentIndex + 1);
  const handleGoToPrevious = () => hasPrevious && setCurrentIndex(currentIndex - 1);

  const handleClose = () => {
    setCurrentIndex(0);
    onClose();
  };

  const handleOpenNews = () => {
    history.push(PATHS.NEWS_DASHBOARD);
    handleClose();
  };

  if (!articlesToDisplay[currentIndex]) return null;

  const displayLocations = getLocationNames(currentArticle);
  const displayDealers = getDealerNames(currentArticle);

  return (
    <Modal open={isOpen} onClose={handleClose} className="NewsModal" size="large">
      <Modal.Header>
        <div className="header-container">
          <div className="header-title-container">
            <div className="header-title">{currentArticle.title}</div>
            {getCategoryLabel(currentArticle) && <Label className="category-label">{getCategoryLabel(currentArticle)}</Label>}
          </div>
          <Button className="modal-close-btn" basic onClick={handleClose}>
            <Icon className="xmark grey" />
          </Button>
        </div>
      </Modal.Header>

      <Modal.Content>
        <div className="article-container">
          {currentArticle.user && (
            <div className="article-author-container">
              <div
                className="profile-circle"
                style={{
                  backgroundImage: `url(${currentArticle.user.profile_picture})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }}
              />
              <span className="author">
                {currentArticle.user.first_name} {currentArticle.user.last_name}
              </span>
              {currentArticle.publish_date && <span className="updated-label">{format(currentArticle.publish_date, DATE_FORMATS_FNS.dateMonthYear)}</span>}
            </div>
          )}
          {displayDealers && (
            <h4 className="article-subtitle">
              {t("v8_dealer", "Dealer")}: {displayDealers}
            </h4>
          )}
          {displayLocations && (
            <h4 className="article-subtitle">
              {t("v8_location", "Location")}: {displayLocations}
            </h4>
          )}

          <div className="article-content-wrapper">
            <div className="article-content" dangerouslySetInnerHTML={{ __html: currentArticle.content }} />
          </div>
        </div>
      </Modal.Content>

      {Boolean(articles) && (
        <Modal.Actions>
          <div className="left-actions">
            {showPagination && (
              <>
                <Button icon basic disabled={!hasPrevious} onClick={handleGoToPrevious}>
                  <Icon className="chevron left" />
                </Button>
                <span className="pagination-info">
                  {currentIndex + 1} {t("v8_of", "of")} {articlesToDisplay.length}
                </span>
                <Button icon basic disabled={!hasNext} onClick={handleGoToNext}>
                  <Icon className="chevron right" />
                </Button>
              </>
            )}
          </div>

          <div className="right-actions">
            <Can I="browse" the="news-page">
              <Button color="green" onClick={handleOpenNews}>
                {t("v8_open_news", "Open News")}
              </Button>
            </Can>
          </div>
        </Modal.Actions>
      )}
    </Modal>
  );
};
